'use client';

import * as React from 'react';
import { Plus, Mic, Send, Camera, ImageIcon, Paperclip } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useI18n } from '@/shared/providers/I18nProvider';

type ChatInputProps = {
  value: string;
  onChange: (value: string) => void;
  onSend: () => Promise<void>;
  isBusy: boolean;
};

export function ChatInput({ value, onChange, onSend, isBusy }: ChatInputProps) {
  const { dict } = useI18n();
  const [showAttachments, setShowAttachments] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const handleSend = async () => {
    if (value.trim() && !isBusy) {
      await onSend();
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      await handleSend();
    }
  };

  React.useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [value]);

  const attachmentOptions = [
    { icon: Camera, label: dict.chat.input.attachments.camera, color: 'text-orange-500' },
    { icon: ImageIcon, label: dict.chat.input.attachments.photo, color: 'text-green-500' },
    { icon: Paperclip, label: dict.chat.input.attachments.file, color: 'text-blue-500' },
  ];

  return (
    <div className='bg-card/80 border-border/50 sticky bottom-0 border-t backdrop-blur-xl'>
      {/* Attachment Options */}
      <div
        className={cn(
          'overflow-hidden transition-all duration-300 ease-out',
          showAttachments ? 'max-h-24 opacity-100' : 'max-h-0 opacity-0',
        )}
      >
        <div className='border-border/30 flex items-center gap-4 border-b px-4 py-3'>
          {attachmentOptions.map((option) => (
            <button
              key={option.label}
              className='hover:bg-secondary/80 flex flex-col items-center gap-1 rounded-xl p-2 transition-colors'
              aria-label={option.label}
            >
              <div className='bg-secondary flex h-10 w-10 items-center justify-center rounded-full'>
                <option.icon className={cn('h-5 w-5', option.color)} />
              </div>
              <span className='text-muted-foreground text-xs'>{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Input Area */}
      <div className='flex items-end gap-2 p-3'>
        <button
          onClick={() => setShowAttachments(!showAttachments)}
          className={cn(
            'rounded-full p-2.5 transition-all duration-200',
            showAttachments
              ? 'bg-primary text-primary-foreground rotate-45'
              : 'bg-secondary text-muted-foreground hover:bg-secondary/80',
          )}
          aria-label={dict.chat.input.attachFileAria}
        >
          <Plus className='h-5 w-5' />
        </button>

        <div className='relative flex flex-1'>
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={dict.chat.input.placeholder}
            rows={1}
            disabled={isBusy}
            className='bg-secondary/80 text-foreground placeholder:text-muted-foreground focus:ring-primary/30 max-h-[120px] w-full resize-none rounded-3xl px-4 py-2.5 text-[15px] transition-all outline-none focus:ring-2'
          />
        </div>

        <button
          onClick={value.trim() ? handleSend : undefined}
          disabled={isBusy}
          className={cn(
            'rounded-full p-2.5 transition-all duration-200',
            value.trim()
              ? 'bg-primary text-primary-foreground scale-100'
              : 'bg-secondary text-muted-foreground scale-95',
          )}
          aria-label={value.trim() ? dict.chat.input.sendAria : dict.chat.input.voiceMessageAria}
        >
          {value.trim() ? <Send className='h-5 w-5' /> : <Mic className='h-5 w-5' />}
        </button>
      </div>
    </div>
  );
}
